/**
 * Ferme les sessions caisse « fantômes » : employé supprimé, désactivé ou absent du workers_* actuel.
 * N'altère pas isCashRegisterLocked — nettoie le stockage avant la même lecture qu'avant.
 */

import { publishWorkerSessionUpdated } from '../infrastructure/events/legacyEventBridge';
import { getCashRegisterRepository } from '../app/di/container';
import { loadWorkersForBusiness } from './workersStorage';
import { loadRoleForBusiness } from './roleStorage';
import {
  loadOpenWorkerSessionsFromKeys,
  loadSessionsForWorker,
  loadSessionsFromKey,
  listWorkerSessionKeysForBusiness,
  persistWorkerSessions
} from './workerSessionsStorage';
import {
  clearCashRegisterPaused,
  getCashRegisterPaused
} from './cashRegisterPause';

function closeOpenSessionEntry(session, nowIso) {
  if (!session || session.logoutTime) return false;
  session.logoutTime = nowIso;
  session.status = 'completed';
  session.finalCashAmount =
    session.finalCashAmount ??
    session.finalAmount ??
    session.cashAmount ??
    session.initialAmount ??
    0;
  session.autoClosedAt = nowIso;
  return true;
}

function resolveActiveWorkerId(businessId) {
  try {
    const repo = getCashRegisterRepository();
    const ncrActive = repo.getActiveSession(businessId);
    if (ncrActive && !ncrActive.logoutTime && ncrActive.workerId != null) {
      return String(ncrActive.workerId);
    }
  } catch {
    /* ignore */
  }

  try {
    const { role } = loadRoleForBusiness(businessId);
    if (role?.type === 'worker' && role.id != null) {
      return String(role.id);
    }
  } catch {
    /* ignore */
  }

  return null;
}

/** IDs des travailleurs actifs pour l'entreprise courante (workers_*). */
export function getKnownActiveWorkerIds(businessId) {
  const ids = new Set();
  if (!businessId) return ids;

  const { workers } = loadWorkersForBusiness(businessId);
  (Array.isArray(workers) ? workers : []).forEach((worker) => {
    if (!worker?.id) return;
    if (worker.isActive === false) return;
    ids.add(String(worker.id));
  });
  return ids;
}

function closeOpenSessionsForWorker(businessId, workerId, nowIso) {
  const sessions = loadSessionsForWorker(businessId, workerId);
  let changed = false;

  sessions.forEach((session) => {
    if (!session || session.logoutTime) return;
    session.logoutTime = nowIso;
    session.status = 'completed';
    session.finalCashAmount =
      session.finalCashAmount ??
      session.finalAmount ??
      session.cashAmount ??
      session.initialAmount ??
      0;
    changed = true;
  });

  if (changed) {
    persistWorkerSessions(businessId, workerId, sessions);
  }
  return changed;
}

function closeOpenSessionsInKey(businessId, key, nowIso) {
  const sessions = loadSessionsFromKey(key);
  let changed = false;

  sessions.forEach((session) => {
    if (!session || session.logoutTime) return;
    session.logoutTime = nowIso;
    session.status = 'completed';
    session.finalCashAmount =
      session.finalCashAmount ??
      session.finalAmount ??
      session.cashAmount ??
      session.initialAmount ??
      0;
    changed = true;
  });

  if (changed) {
    const workerId = sessions.find((s) => s?.workerId)?.workerId;
    if (workerId) {
      persistWorkerSessions(businessId, workerId, sessions);
    }
  }
  return changed;
}

/**
 * @param {string} businessId
 * @param {Set<string>|null} [knownWorkerIds] — si omis, chargé depuis workers_*
 * @returns {{ pruned: number, details: string[] }}
 */
export function pruneStaleWorkerSessions(businessId, knownWorkerIds = null) {
  const details = [];
  if (!businessId) return { pruned: 0, details };

  const validIds =
    knownWorkerIds instanceof Set ? knownWorkerIds : getKnownActiveWorkerIds(businessId);
  const nowIso = new Date().toISOString();
  let pruned = 0;

  const seenWorkers = new Set();

  loadOpenWorkerSessionsFromKeys(businessId).forEach((session) => {
    const workerId = String(session?.workerId || '');
    if (!workerId || validIds.has(workerId) || seenWorkers.has(workerId)) return;
    seenWorkers.add(workerId);
    if (closeOpenSessionsForWorker(businessId, workerId, nowIso)) {
      pruned += 1;
      details.push(`worker_sessions:${workerId}`);
    }
  });

  (listWorkerSessionKeysForBusiness(businessId) || []).forEach((key) => {
    const sessions = loadSessionsFromKey(key);
    const hasOpen = sessions.some((s) => s && !s.logoutTime);
    if (!hasOpen) return;

    const workerId = String(sessions.find((s) => s?.workerId)?.workerId || '');
    if (workerId && validIds.has(workerId)) return;

    if (closeOpenSessionsInKey(businessId, key, nowIso)) {
      pruned += 1;
      details.push(`key:${key}`);
    }
  });

  try {
    const repo = getCashRegisterRepository();
    const active = repo.getActiveSession(businessId);
    if (active && !active.logoutTime) {
      const activeWorkerId = String(active.workerId || '');
      if (activeWorkerId && !validIds.has(activeWorkerId)) {
        repo.forceClearActiveSession(businessId);
        repo.repairOrphanSessions(businessId);
        pruned += 1;
        details.push(`newCashRegisterActive:${activeWorkerId}`);
      }
    } else {
      repo.repairOrphanSessions(businessId);
    }
  } catch (error) {
    console.warn('[workerSessionSanitizer] cash register cleanup:', error?.message || error);
  }

  try {
    const pause = getCashRegisterPaused(businessId);
    const pauseWorkerId = String(pause?.workerId || '');
    if (pauseWorkerId && !validIds.has(pauseWorkerId)) {
      clearCashRegisterPaused(businessId);
      pruned += 1;
      details.push(`pause:${pauseWorkerId}`);
    }
  } catch (error) {
    console.warn('[workerSessionSanitizer] pause cleanup:', error?.message || error);
  }

  if (pruned > 0) {
    publishWorkerSessionUpdated({ businessId, action: 'prune_stale', pruned, details });
  }

  return { pruned, details };
}

/**
 * Closes ghost open sessions that must not remain as "active":
 * - duplicate open rows for the same worker (keeps the latest)
 * - open sessions of a different worker when one active worker owns the caisse
 *
 * Does NOT invent a counted close for the sole open session of a known cashier
 * when no one is active in the UI (manager after full logout, crash, etc.).
 * Those stay open until the cashier settles via the logout cash modal.
 *
 * @param {string} businessId
 * @param {string|null|undefined} activeWorkerId
 *   - undefined: resolve from NCR active / stored worker role
 *   - string: keep that worker
 *   - null: no active cashier UI — keep known workers' open shifts (collapse dupes only)
 */
export function reconcileAbandonedWorkerSessions(businessId, activeWorkerId = undefined) {
  const details = [];
  if (!businessId) return { closed: 0, details };

  const nowIso = new Date().toISOString();
  const keepWorkerId =
    activeWorkerId === undefined
      ? resolveActiveWorkerId(businessId)
      : activeWorkerId
        ? String(activeWorkerId)
        : null;

  const knownIds = getKnownActiveWorkerIds(businessId);
  let closed = 0;
  const touchedWorkers = new Set();

  (listWorkerSessionKeysForBusiness(businessId) || []).forEach((key) => {
    const sessions = loadSessionsFromKey(key);
    const workerId = String(sessions.find((s) => s?.workerId)?.workerId || '');
    if (!workerId) return;

    const openIndices = sessions
      .map((session, index) => (!session?.logoutTime ? index : -1))
      .filter((index) => index >= 0);

    if (!openIndices.length) return;

    let changed = false;

    if (keepWorkerId && workerId === keepWorkerId) {
      // Same active cashier: keep latest open only
      if (openIndices.length > 1) {
        openIndices.slice(0, -1).forEach((index) => {
          if (closeOpenSessionEntry(sessions[index], nowIso)) {
            closed += 1;
            changed = true;
          }
        });
      }
    } else if (keepWorkerId && workerId !== keepWorkerId) {
      // Another cashier holds the lock — force-close this worker's open rows
      openIndices.forEach((index) => {
        if (closeOpenSessionEntry(sessions[index], nowIso)) {
          closed += 1;
          changed = true;
        }
      });
    } else if (knownIds.size > 0 && !knownIds.has(workerId)) {
      // Unknown / removed worker — safe to auto-close
      openIndices.forEach((index) => {
        if (closeOpenSessionEntry(sessions[index], nowIso)) {
          closed += 1;
          changed = true;
        }
      });
    } else {
      // No active cashier in UI: keep open session for count-at-logout (collapse dupes only)
      if (openIndices.length > 1) {
        openIndices.slice(0, -1).forEach((index) => {
          if (closeOpenSessionEntry(sessions[index], nowIso)) {
            closed += 1;
            changed = true;
          }
        });
      }
    }

    if (changed) {
      persistWorkerSessions(businessId, workerId, sessions);
      touchedWorkers.add(workerId);
      details.push(`worker:${workerId}`);
    }
  });

  try {
    const repo = getCashRegisterRepository();
    const ncrActive = repo.getActiveSession(businessId);
    if (ncrActive && !ncrActive.logoutTime) {
      const ncrWorkerId = String(ncrActive.workerId || '');
      if (keepWorkerId) {
        if (ncrWorkerId !== keepWorkerId) {
          repo.forceClearActiveSession(businessId);
          repo.repairOrphanSessions(businessId);
          details.push(`ncrActive:${ncrWorkerId || 'unknown'}`);
        }
      } else if (knownIds.size > 0 && ncrWorkerId && !knownIds.has(ncrWorkerId)) {
        repo.forceClearActiveSession(businessId);
        repo.repairOrphanSessions(businessId);
        details.push(`ncrActive:stale:${ncrWorkerId}`);
      }
      // else: preserve NCR lock so caisse stays locked until counted logout
    } else if (keepWorkerId) {
      repo.repairOrphanSessions(businessId);
    }
  } catch (error) {
    console.warn('[workerSessionSanitizer] reconcile NCR:', error?.message || error);
  }

  if (closed > 0) {
    publishWorkerSessionUpdated({ businessId, action: 'reconcile_abandoned', closed, details });
  }

  return { closed, details, keepWorkerId };
}
